
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Menu, X } from 'lucide-react';

function Header() {
  const [isCursosOpen, setIsCursosOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const desktopCursosMenuId = 'desktop-cursos-menu';
  const mobileCursosMenuId = 'mobile-cursos-menu';

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsCursosOpen(false);
  }, [location.pathname]);

  const cursosItems = [
    { label: 'Curso 0-4 meses', path: '/cursos/0-4-meses' },
    { label: 'Curso 4-20 meses', path: '/cursos/4-20-meses' },
    { label: 'Destete Nocturno', path: '/cursos/destete-nocturno' },
    { label: 'Lo que necesitás saber cuando nace el bebé', path: '/cursos/lo-que-necesitamos-saber' },
    { label: 'Primeros Alimentos', path: '/cursos/primeros-alimentos' },
    { label: 'Combos', path: '/cursos/combos' }
  ];

  const navItems = [
    { label: 'Nosotras', path: '/nosotras' },
    { label: 'Consultorías', path: '/consultorias' },
    { type: 'dropdown', label: 'Cursos', path: '/cursos', items: cursosItems },
    { label: 'Podcast', path: '/podcast' },
    { label: 'Contacto', path: '/contacto' },
    { label: 'Blog', path: '/blog' }
  ];

  const isActive = (path) => location.pathname === path || (path !== '/' && location.pathname.startsWith(path));

  return (
    <header className="sticky top-0 z-50 bg-background shadow-sm shadow-primary/5 lg:relative lg:shadow-none">
      <div className="hidden max-w-7xl mx-auto px-4 pt-5 sm:px-6 lg:block lg:px-8 lg:pt-6">
        <div className="flex justify-end">
          <a
            href="https://lucilapistiner.wisboo.com/signin"
            className="rounded-full bg-primary px-5 py-2 font-rocky text-lg tracking-wide text-primary-foreground transition-colors hover:bg-primary/90 active:scale-[0.98]"
          >
            Acceder a tu cuenta
          </a>
        </div>
      </div>

      <div className="py-3 lg:pb-10 lg:pt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Elegant Signature Logo */}
            <Link 
              to="/" 
              className="flex-shrink-0"
            >
              <img 
                src="/logolupistiner.webp" 
                alt="Lucila Pistiner logo" 
                className="h-10 object-contain md:h-12 lg:h-20"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-16">
              {navItems.map((item) => (
                item.type === 'dropdown' ? (
                  <div 
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => setIsCursosOpen(true)}
                    onMouseLeave={() => setIsCursosOpen(false)}
                  >
                    <div className={`font-rocky text-2xl tracking-wide transition-all duration-300 flex items-center gap-1 ${
                      isActive(item.path) ? 'text-primary' : 'text-foreground hover:text-primary'
                    }`}>
                      <Link to={item.path} aria-current={isActive(item.path) ? 'page' : undefined}>{item.label}</Link>
                      <button
                        type="button"
                        onClick={() => setIsCursosOpen(!isCursosOpen)}
                        className="flex min-h-11 min-w-11 items-center justify-center rounded-full"
                        aria-label="Abrir menú de cursos"
                        aria-expanded={isCursosOpen}
                        aria-haspopup="menu"
                        aria-controls={desktopCursosMenuId}
                      >
                        <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isCursosOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </div>

                    <AnimatePresence>
                      {isCursosOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                          id={desktopCursosMenuId}
                          role="menu"
                          className="absolute top-full left-0 mt-2 w-64 bg-background border border-primary/10 rounded overflow-hidden z-50"
                        >
                          {item.items.map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              role="menuitem"
                              className="block px-6 py-4 font-rocky text-xl tracking-wide hover:bg-accent hover:text-primary transition-colors duration-200"
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    aria-current={isActive(item.path) ? 'page' : undefined}
                    className={`font-rocky text-2xl tracking-wide transition-all duration-300 relative ${
                      isActive(item.path) ? 'text-primary' : 'text-foreground hover:text-primary'
                    }`}
                  >
                    {item.label}
                  </Link>
                )
              ))}
            </nav>

            {/* Mobile Menu Toggle */}
            <button
              type="button"
              className="lg:hidden p-2 text-foreground hover:text-primary transition-colors duration-300"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-navigation"
            >
              {isMobileMenuOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              <motion.button
                type="button"
                aria-label="Cerrar menú"
                className="fixed inset-0 z-40 bg-[#2D120C]/35 lg:hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsMobileMenuOpen(false)}
              />
              <motion.aside
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                className="fixed right-0 top-0 z-50 h-dvh w-[85vw] max-w-sm overflow-y-auto bg-background px-6 py-5 shadow-2xl lg:hidden"
              >
                <div className="mb-7 flex items-center justify-between">
                  <Link to="/" className="flex-shrink-0" aria-label="Ir al inicio">
                    <img
                      src="/logolupistiner.webp"
                      alt="Lucila Pistiner logo"
                      className="h-10 object-contain"
                    />
                  </Link>
                  <button
                    type="button"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="rounded-full p-2 text-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                    aria-label="Cerrar menú"
                  >
                    <X className="h-7 w-7" />
                  </button>
                </div>

                <nav id="mobile-navigation" className="flex flex-col space-y-4">
                  <a
                    href="https://lucilapistiner.wisboo.com/signin"
                    className="mb-3 rounded-full bg-primary px-5 py-3 text-center font-rocky text-xl tracking-wide text-primary-foreground transition-colors hover:bg-primary/90"
                  >
                    Acceder a tu cuenta
                  </a>

                  {navItems.map((item) => (
                    item.type === 'dropdown' ? (
                      <div key={item.label} className="space-y-2">
                        <div
                          className={`font-rocky text-3xl tracking-wide w-full text-left flex items-center justify-between ${
                            isActive(item.path) ? 'text-primary' : 'text-foreground'
                          }`}
                        >
                          <Link to={item.path} className="flex-1">
                            {item.label}
                          </Link>
                          <button
                            type="button"
                            onClick={() => setIsCursosOpen(!isCursosOpen)}
                            className="flex min-h-11 min-w-11 items-center justify-center rounded-full p-1 text-inherit transition-colors hover:bg-primary/10"
                            aria-label="Abrir menú de cursos"
                            aria-expanded={isCursosOpen}
                            aria-haspopup="menu"
                            aria-controls={mobileCursosMenuId}
                          >
                            <ChevronDown className={`w-6 h-6 transition-transform duration-300 ${isCursosOpen ? 'rotate-180' : ''}`} />
                          </button>
                        </div>
                        <AnimatePresence>
                          {isCursosOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              id={mobileCursosMenuId}
                              role="menu"
                              className="flex flex-col pl-4 space-y-3 pt-2 overflow-hidden border-l-2 border-primary/20 ml-2"
                            >
                              {item.items.map((subItem) => (
                                <Link
                                  key={subItem.path}
                                  to={subItem.path}
                                  role="menuitem"
                                  className="font-rocky text-2xl tracking-wide text-foreground/80 hover:text-primary transition-colors"
                                >
                                  {subItem.label}
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        key={item.path}
                        to={item.path}
                        aria-current={isActive(item.path) ? 'page' : undefined}
                        className={`font-rocky text-3xl tracking-wide transition-colors ${
                          isActive(item.path) ? 'text-primary' : 'text-foreground hover:text-primary'
                        }`}
                      >
                        {item.label}
                      </Link>
                    )
                  ))}
                </nav>
              </motion.aside>
            </>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}

export default Header;
